import { useEffect, useRef } from 'react';
import * as monaco from 'monaco-editor';
import { validateFile } from './monacoUtils';

/**
 * A request to scroll the editor to a specific line and place the cursor
 * there. `nonce` makes each request distinct so clicking the same target
 * twice (e.g. after scrolling away) re-reveals it.
 */
export interface RevealTarget {
  line: number;
  column?: number;
  nonce: number;
}

interface MonacoEditorProps {
  /** Every open model, by the file's tree path. */
  models: React.MutableRefObject<Map<string, monaco.editor.ITextModel>>;
  modelsReady: boolean; // Signal that models have been created
  /** Changes when a load replaced every model — the editor rebinds even when it shows the same path. */
  modelsVersion: number;
  currentPath: string | null;
  /** Whether a file opens read-only (the registries the site rewrites). */
  isReadOnly: (path: string) => boolean;
  onErrorsChange: (path: string, errors: string[]) => void;
  /** When set (with a fresh `nonce`), scroll/jump to this line. */
  revealTarget?: RevealTarget | null;
}

export function MonacoEditor({
  models,
  modelsReady,
  modelsVersion,
  currentPath,
  isReadOnly,
  onErrorsChange,
  revealTarget,
}: MonacoEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const validationTimeoutRef = useRef<number | null>(null);
  // Last reveal we acted on — so an unrelated re-render (e.g. switching files
  // via the file tree) doesn't re-jump to a stale target.
  const lastRevealNonce = useRef<number>(-1);

  // Create editor when models are ready
  useEffect(() => {
    if (!containerRef.current || !modelsReady) return;
    
    // Don't recreate if editor already exists
    if (editorRef.current) return;

    const model = currentPath ? models.current.get(currentPath) : undefined;
    
    // Check if model is valid (not disposed)
    if (!currentPath || !model || model.isDisposed()) {
      console.warn('[MonacoEditor] Model is disposed or not found, skipping editor creation');
      return;
    }

    console.log('[MonacoEditor] Creating editor with model:', currentPath);
    
    const editor = monaco.editor.create(containerRef.current, {
      model: model,
      readOnly: isReadOnly(currentPath),
      theme: 'vs-dark',
      fontSize: 14,
      fontFamily: "'Fira Code', Consolas, 'Courier New', monospace",
      fontLigatures: true,
      minimap: { enabled: false },
      automaticLayout: true,
      scrollBeyondLastLine: false,
      padding: { top: 10 },
      lineNumbers: 'on',
      renderLineHighlight: 'line',
      tabSize: 2,
      formatOnPaste: true,
      formatOnType: true,
    });

    editorRef.current = editor;

    return () => {
      editorRef.current = null;
      editor.dispose();
    };
  }, [modelsReady, modelsVersion, currentPath, models, isReadOnly]);

  // Validate the open file once its edits pause (each model's edits already flow into the project tree)
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !currentPath) return;

    const disposable = editor.onDidChangeModelContent(() => {
      // Debounced validation
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
      validationTimeoutRef.current = window.setTimeout(async () => {
        const errors = await validateFile(models.current, currentPath);
        onErrorsChange(currentPath, errors);
      }, 500);
    });

    return () => {
      disposable.dispose();
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, [currentPath, models, onErrorsChange]);

  // React immediately to Monaco marker changes (red squiggles)
  useEffect(() => {
    const disposable = monaco.editor.onDidChangeMarkers(async (changedUris) => {
      if (changedUris.length === 0) return;

      const changed = new Set(changedUris.map((uri) => uri.toString()));
      const validations: Promise<void>[] = [];

      models.current.forEach((model, path) => {
        if (!changed.has(model.uri.toString())) return;

        validations.push((async () => {
          const errors = await validateFile(models.current, path);
          onErrorsChange(path, errors);
        })());
      });

      if (validations.length > 0) {
        await Promise.all(validations);
      }
    });

    return () => disposable.dispose();
  }, [models, onErrorsChange]);

  // Switch model when the open file changes
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !currentPath) return;

    const model = models.current.get(currentPath);
    if (model) {
      editor.setModel(model);
      editor.updateOptions({ readOnly: isReadOnly(currentPath) });
    }
  }, [currentPath, models, isReadOnly]);

  // Scroll/jump to a requested line. Runs AFTER the model-switch effect above
  // (declaration order), so the target line is revealed on the freshly-switched
  // model. The nonce guard ensures we only act on NEW requests, not on every
  // re-render that happens to carry the same target.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !revealTarget) return;
    if (revealTarget.nonce === lastRevealNonce.current) return;

    // Defensive: make sure the model for the current file is applied
    // before revealing (the file may have changed in the same commit).
    const model = currentPath ? models.current.get(currentPath) : undefined;
    if (model && editor.getModel() !== model) editor.setModel(model);

    lastRevealNonce.current = revealTarget.nonce;
    const lineNumber = revealTarget.line;
    const column = revealTarget.column ?? 1;
    editor.revealLineInCenter(lineNumber);
    editor.setPosition({ lineNumber, column });
    editor.focus();
  }, [revealTarget, currentPath, models]);

  // Stop propagation on keyboard events to prevent game input interference
  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      ref={containerRef}
      id="editor-container"
      style={{ flex: 1, overflow: 'hidden' }}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
    />
  );
}
